import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Supabase client'ı oluştur
const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Room {
  id: string;
  name: string;
  capacity: number;
  color: string;
}

interface Reservation {
  id: string;
  room_id: string;
  meeting_date: string;
  meeting_time: string;
  title: string;
  organizer: string;
  participants: number;
  password: string;
  duration: number; // Toplantı süresi (saat cinsinden)
  catering_enabled?: boolean; // İkram seçeneği
  catering_type?: string; // İkram türü
  catering_notes?: string; // İkram notları
}

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

const durationOptions = [
  { value: 0.5, label: '30 dakika' },
  { value: 1, label: '1 saat' },
  { value: 1.5, label: '1.5 saat' },
  { value: 2, label: '2 saat' },
  { value: 3, label: '3 saat' },
  { value: 4, label: '4 saat' },
  { value: 8, label: 'Tüm gün (8 saat)' }
];

const cateringOptions = [
  { value: 'pasta', label: 'Pasta' },
  { value: 'cerez', label: 'Çerez' },
  { value: 'kahve', label: 'Kahve & Çay' },
  { value: 'sandvic', label: 'Sandviç' },
  { value: 'meyve', label: 'Meyve Tabağı' },
  { value: 'tatli', label: 'Tatlı' },
  { value: 'diger', label: 'Diğer' }
];

export default function MeetingScheduler() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [organizer, setOrganizer] = useState('');
  const [meetingPassword, setMeetingPassword] = useState('');
  const [participants, setParticipants] = useState(1);
  const [meetingDuration, setMeetingDuration] = useState(0.5); // Yeni: toplantı süresi
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReservationId, setCancelReservationId] = useState('');
  const [cancelOrganizerName, setCancelOrganizerName] = useState('');
  const [cancelPassword, setCancelPassword] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [relatedTimeSlots, setRelatedTimeSlots] = useState<string[]>([]);
  const [selectedSlotsToCancel, setSelectedSlotsToCancel] = useState<string[]>([]);
  const [cateringEnabled, setCateringEnabled] = useState(false);
  const [cateringType, setCateringType] = useState('');
  const [cateringNotes, setCateringNotes] = useState('');

  // Auth token'ı localStorage'dan al veya basit token oluştur
  const getAuthToken = async () => {
    try {
      // Basit bir token sistemi kullan
      let token = localStorage.getItem('simple_auth_token');
      
      if (!token) {
        // Basit token oluştur
        token = 'simple_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('simple_auth_token', token);
      }
      
      return token;
    } catch (error) {
      console.error('Token alma hatası:', error);
      // Hata durumunda yeni token oluştur
      const newToken = 'simple_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('simple_auth_token', newToken);
      return newToken;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Veri yükleniyor...', selectedDate);
      
      const token = await getAuthToken();
      if (!token) {
        setError('Kimlik doğrulama hatası, sayfa yenileniyor...');
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-reservations?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        
        // Token geçersizse yeni token al ve tekrar dene
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          const newToken = await getAuthToken();
          
          const retryResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-reservations?date=${selectedDate}`, {
            headers: {
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            // Update room names here
            const updatedRooms = data.rooms?.map((room: Room) => ({
              ...room,
              name: room.name === 'Ankara' ? 'Connect' : room.name
            })) || [];
            setRooms(updatedRooms);
            setReservations(data.reservations || []);
            return;
          }
        }
        
        throw new Error('Veri yüklenemedi');
      }
      
      const data = await response.json();
      console.log('API Response Data:', data);
      
      // Update room names here
      const updatedRooms = data.rooms?.map((room: Room) => ({
        ...room,
        name: room.name === 'Ankara' ? 'Connect' : room.name
      })) || [];
      
      setRooms(updatedRooms);
      setReservations(data.reservations || []);
      
      console.log('Yüklenen odalar:', updatedRooms);
      console.log('Yüklenen rezervasyonlar:', data.reservations);
      
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  // Belirli bir saat diliminin rezerve olup olmadığını kontrol et
  const isSlotBooked = (roomId: string, time: string) => {
    return reservations.some(reservation => {
      const dbTime = reservation.meeting_time.substring(0, 5);
      const reservationStartTime = timeSlots.indexOf(dbTime);
      const reservationEndTime = reservationStartTime + Math.floor(reservation.duration * 2); // Math.floor eklendi
      const currentSlotIndex = timeSlots.indexOf(time);
      
      return reservation.room_id === roomId && 
             reservation.meeting_date === selectedDate &&
             currentSlotIndex >= reservationStartTime &&
             currentSlotIndex < reservationEndTime;
    });
  };

  // Belirli bir saat dilimi için rezervasyon bilgisini getir
  const getReservationForSlot = (roomId: string, time: string) => {
    return reservations.find(reservation => {
      const dbTime = reservation.meeting_time.substring(0, 5);
      const reservationStartTime = timeSlots.indexOf(dbTime);
      const reservationEndTime = reservationStartTime + Math.floor(reservation.duration * 2); // Math.floor eklendi
      const currentSlotIndex = timeSlots.indexOf(time);
      
      return reservation.room_id === roomId && 
             reservation.meeting_date === selectedDate &&
             currentSlotIndex >= reservationStartTime &&
             currentSlotIndex < reservationEndTime;
    });
  };

  // Rezervasyonun başlangıç slotu olup olmadığını kontrol et
  const isStartingSlot = (roomId: string, time: string) => {
    const reservation = getReservationForSlot(roomId, time);
    if (!reservation) return false;
    
    const dbTime = reservation.meeting_time.substring(0, 5);
    return dbTime === time;
  };

  // Seçilen süre için tüm slotların müsait olup olmadığını kontrol et
  const canBookDuration = (roomId: string, startTime: string, duration: number) => {
    const startIndex = timeSlots.indexOf(startTime);
    const slotsNeeded = Math.floor(duration * 2); // Math.floor eklendi
    
    // Zaman aralığı kontrolü
    if (startIndex + slotsNeeded > timeSlots.length) {
      return false;
    }
    
    // Her slot için rezervasyon kontrolü
    for (let i = 0; i < slotsNeeded; i++) {
      const slotTime = timeSlots[startIndex + i];
      if (isSlotBooked(roomId, slotTime)) {
        return false;
      }
    }
    
    return true;
  };

  const handleSlotClick = (roomId: string, time: string) => {
    console.log(`Slot tıklandı: ${roomId}-${time}`);
    
    // Rezerve edilmiş slotlara tıklanamaz
    if (isSlotBooked(roomId, time)) {
      setError('Bu saat dilimi zaten rezerve edilmiş!');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    setSelectedRoom(roomId);
    setSelectedTime(time);
    setMeetingDuration(0.5); // Varsayılan süre
    setCateringEnabled(false); // İkram seçeneğini sıfırla
    setCateringType(''); // İkram türünü sıfırla
    setCateringNotes(''); // İkram notlarını sıfırla
    setError('');
    setShowModal(true);
  };

  const handleBookMeeting = async () => {
    if (!meetingTitle || !organizer || !meetingPassword) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    if (meetingPassword.length < 4) {
      setError('Şifre en az 4 karakter olmalıdır');
      return;
    }

    if (cateringEnabled && !cateringType) {
      setError('Lütfen ikram türünü seçin');
      return;
    }

    // Seçilen süre için tüm slotların müsait olup olmadığını kontrol et
    if (!canBookDuration(selectedRoom, selectedTime, meetingDuration)) {
      setError('Seçilen süre için bazı saat dilimleri müsait değil!');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const timeForDb = selectedTime + ':00';
      const token = await getAuthToken();
      
      if (!token) {
        setError('Kimlik doğrulama hatası, lütfen sayfayı yenileyin');
        return;
      }
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-reservation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          room_id: selectedRoom,
          meeting_date: selectedDate,
          meeting_time: timeForDb,
          title: meetingTitle,
          organizer,
          participants,
          password: meetingPassword,
          duration: meetingDuration,
          catering_enabled: cateringEnabled,
          catering_type: cateringEnabled ? cateringType : null,
          catering_notes: cateringEnabled ? cateringNotes : null
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Token geçersizse yeni token al ve tekrar dene
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          const newToken = await getAuthToken();
          
          const retryResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-reservation`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              room_id: selectedRoom,
              meeting_date: selectedDate,
              meeting_time: timeForDb,
              title: meetingTitle,
              organizer,
              participants,
              password: meetingPassword,
              duration: meetingDuration,
              catering_enabled: cateringEnabled,
              catering_type: cateringEnabled ? cateringType : null,
              catering_notes: cateringEnabled ? cateringNotes : null
            }),
          });

          const retryResult = await retryResponse.json();
          
          if (!retryResponse.ok) {
            setError(retryResult.error || 'Rezervasyon yapılamadı');
            return;
          }
          
          // Başarılı olursa devam et
        } else {
          setError(result.error || 'Rezervasyon yapılamadı');
          return;
        }
      }

      setShowModal(false);
      setMeetingTitle('');
      setOrganizer('');
      setMeetingPassword('');
      setParticipants(1);
      setMeetingDuration(0.5);
      setCateringEnabled(false);
      setCateringType('');
      setCateringNotes('');
      setError('');
      
      console.log('Rezervasyon başarılı, veriler yenileniyor...');
      await loadData();
    } catch (error) {
      console.error('Rezervasyon hatası:', error);
      setError('Bir hata oluştu, lütfen tekrar deneyin');
    } finally {
      setSubmitting(false);
    }
  };

  // Aynı rezervasyona ait tüm saat dilimlerini getir
  const getRelatedTimeSlots = (reservation: Reservation) => {
    const dbTime = reservation.meeting_time.substring(0, 5);
    const startIndex = timeSlots.indexOf(dbTime);
    const slotsCount = Math.floor(reservation.duration * 2);
    
    const slots = [];
    for (let i = 0; i < slotsCount; i++) {
      if (startIndex + i < timeSlots.length) {
        slots.push(timeSlots[startIndex + i]);
      }
    }
    return slots;
  };

  const handleCancelReservation = async (reservationId: string, organizerName: string) => {
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) return;

    const relatedSlots = getRelatedTimeSlots(reservation);
    
    setCancelReservationId(reservationId);
    setSelectedReservation(reservation);
    setRelatedTimeSlots(relatedSlots);
    setSelectedSlotsToCancel(relatedSlots); // Varsayılan olarak tüm slotlar seçili
    setCancelOrganizerName('');
    setCancelPassword('');
    setShowCancelModal(true);
    setError('');
  };

  const confirmCancelReservation = async () => {
    if (!cancelOrganizerName.trim() || !cancelPassword.trim()) {
      setError('Lütfen organizatör adını ve şifresini girin');
      return;
    }

    if (selectedSlotsToCancel.length === 0) {
      setError('Lütfen iptal etmek istediğiniz en az bir saat dilimi seçin');
      return;
    }

    try {
      setCancelSubmitting(true);
      setError('');

      const token = await getAuthToken();
      
      if (!token) {
        setError('Kimlik doğrulama hatası, lütfen sayfayı yenileyin');
        return;
      }

      // Eğer tüm slotlar seçiliyse, rezervasyonu tamamen sil
      if (selectedSlotsToCancel.length === relatedTimeSlots.length) {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/delete-reservation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            reservation_id: cancelReservationId,
            organizer: cancelOrganizerName.trim(),
            password: cancelPassword.trim()
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('auth_token');
            const newToken = await getAuthToken();
            
            const retryResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/delete-reservation`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${newToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                reservation_id: cancelReservationId,
                organizer: cancelOrganizerName.trim(),
                password: cancelPassword.trim()
              }),
            });

            const retryResult = await retryResponse.json();
            
            if (!retryResponse.ok) {
              setError(retryResult.error || 'Rezervasyon iptal edilemedi');
              return;
            }
          } else {
            setError(result.error || 'Rezervasyon iptal edilemedi');
            return;
          }
        }
      } else {
        // Kısmi iptal için rezervasyonu güncelle
        if (!selectedReservation) return;

        const firstSelectedSlot = selectedSlotsToCancel[0];
        const lastSelectedSlot = selectedSlotsToCancel[selectedSlotsToCancel.length - 1];
        const firstSelectedIndex = timeSlots.indexOf(firstSelectedSlot);
        const lastSelectedIndex = timeSlots.indexOf(lastSelectedSlot);
        
        const originalStartIndex = timeSlots.indexOf(selectedReservation.meeting_time.substring(0, 5));
        const originalEndIndex = originalStartIndex + Math.floor(selectedReservation.duration * 2) - 1;

        // Baştan iptal ediliyorsa
        if (firstSelectedIndex === originalStartIndex) {
          const newStartTime = timeSlots[lastSelectedIndex + 1] + ':00';
          const newDuration = (originalEndIndex - lastSelectedIndex) * 0.5;
          
          const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-reservation`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reservation_id: cancelReservationId,
              organizer: cancelOrganizerName.trim(),
              password: cancelPassword.trim(),
              meeting_time: newStartTime,
              duration: newDuration
            }),
          });

          if (!response.ok) {
            const result = await response.json();
            setError(result.error || 'Rezervasyon güncellenemedi');
            return;
          }
        }
        // Sondan iptal ediliyorsa
        else if (lastSelectedIndex === originalEndIndex) {
          const newDuration = (firstSelectedIndex - originalStartIndex) * 0.5;
          
          const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-reservation`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reservation_id: cancelReservationId,
              organizer: cancelOrganizerName.trim(),
              password: cancelPassword.trim(),
              duration: newDuration
            }),
          });

          if (!response.ok) {
            const result = await response.json();
            setError(result.error || 'Rezervasyon güncellenemedi');
            return;
          }
        } else {
          setError('Sadece baştan veya sondan saat dilimleri iptal edilebilir');
          return;
        }
      }

      setShowCancelModal(false);
      setCancelReservationId('');
      setCancelOrganizerName('');
      setCancelPassword('');
      setSelectedReservation(null);
      setRelatedTimeSlots([]);
      setSelectedSlotsToCancel([]);
      console.log('Rezervasyon iptal edildi, veriler yenileniyor...');
      await loadData();
    } catch (error) {
      console.error('İptal hatası:', error);
      setError('Bir hata oluştu');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const toggleSlotSelection = (slot: string) => {
    setSelectedSlotsToCancel(prev => {
      if (prev.includes(slot)) {
        return prev.filter(s => s !== slot);
      } else {
        return [...prev, slot].sort((a, b) => timeSlots.indexOf(a) - timeSlots.indexOf(b));
      }
    });
  };

  const selectAllSlots = () => {
    setSelectedSlotsToCancel([...relatedTimeSlots]);
  };

  const selectNoneSlots = () => {
    setSelectedSlotsToCancel([]);
  };

  const getRoomColor = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      return room.color;
    }
    
    // Fallback renk sistemi
    const roomIdNum = parseInt(roomId);
    const colors = [
      'bg-teal-100 border-teal-300', // Connect (eski Ankara) - Teal rengi (ID: 1)
      'bg-green-100 border-green-300', // İstanbul (ID: 2)
      'bg-orange-100 border-orange-300', // İzmir (ID: 3)
    ];
    return colors[roomIdNum - 1] || 'bg-gray-100 border-gray-300';
  };

  const getRoomTextColor = (roomId: string) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      // room.color'dan text rengini çıkar
      if (room.color.includes('teal')) return 'text-teal-600';
      if (room.color.includes('green')) return 'text-green-600';
      if (room.color.includes('orange')) return 'text-orange-600';
    }
    
    // Fallback text renk sistemi
    const roomIdNum = parseInt(roomId);
    const colors = [
      'text-teal-600', // Connect (eski Ankara) - Teal text (ID: 1)
      'text-green-600', // İstanbul (ID: 2)
      'text-orange-600', // İzmir (ID: 3)
    ];
    return colors[roomIdNum - 1] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-4xl text-blue-600"></i>
        </div>
      </div>
    );
  }

  console.log('Render - Mevcut rezervasyonlar:', reservations);
  console.log('Render - Mevcut odalar:', rooms);
  console.log('Render - Seçili tarih:', selectedDate);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Hata Mesajı */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Günlük Toplantı Takvimi</h2>
          <p className="text-gray-600">Toplantı odalarını seçin ve rezervasyon yapın</p>
        </div>
        <div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 flex flex-wrap gap-4">
        {rooms.map(room => (
          <div key={room.id} className={`px-3 py-2 rounded-lg border-2 ${room.color}`}>
            <div className="font-medium text-gray-900">{room.name}</div>
            <div className="text-sm text-gray-600">Kapasite: {room.capacity} kişi</div>
          </div>
        ))}
        <div className="px-3 py-2 rounded-lg border-2 bg-red-100 border-red-300">
          <div className="font-medium text-red-800">Rezerve Edilmiş</div>
          <div className="text-sm text-red-600">Müsait değil</div>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header */}
          <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: '80px 1fr 1fr 1fr' }}>
            <div className="font-semibold text-gray-700 p-2 text-sm">Saat</div>
            {rooms.map(room => (
              <div key={room.id} className={`font-semibold text-gray-700 p-3 text-center rounded-lg border-2 ${getRoomColor(room.id)}`}>
                <div className="text-sm font-bold">{room.name}</div>
                <div className="text-xs text-gray-500 mt-1">Kapasite: {room.capacity}</div>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          {timeSlots.map(time => (
            <div key={time} className="grid gap-2 mb-2" style={{ gridTemplateColumns: '80px 1fr 1fr 1fr' }}>
              <div className="font-medium text-gray-700 p-2 bg-gray-100 rounded-lg flex items-center justify-center text-sm border">
                {time}
              </div>
              {rooms.map(room => {
                const isBooked = isSlotBooked(room.id, time);
                const reservation = getReservationForSlot(room.id, time);
                const isStarting = isStartingSlot(room.id, time);
                
                return (
                  <div
                    key={`${room.id}-${time}`}
                    className={`p-2 rounded-lg border-2 transition-all min-h-[80px] ${
                      isBooked 
                        ? 'bg-red-50 border-red-300 cursor-not-allowed' 
                        : `${getRoomColor(room.id)} hover:opacity-80 cursor-pointer hover:shadow-md hover:scale-[1.02]`
                    }`}
                    onClick={!isBooked ? () => handleSlotClick(room.id, time) : undefined}
                  >
                    {isBooked && reservation ? (
                      <div className="text-xs h-full flex flex-col">
                        {isStarting ? (
                          <>
                            <div className="font-semibold text-red-900 mb-1 text-sm truncate" title={reservation.title}>
                              {reservation.title}
                            </div>
                            <div className="text-red-700 text-xs mb-1 flex items-center">
                              <i className="ri-user-line mr-1 flex-shrink-0"></i>
                              <span className="truncate">{reservation.organizer}</span>
                            </div>
                            <div className="text-red-700 text-xs mb-1 flex items-center">
                              <i className="ri-time-line mr-1 flex-shrink-0"></i>
                              <span>{reservation.duration >= 1 ? `${reservation.duration} saat` : `${reservation.duration * 60} dk`}</span>
                            </div>
                            <div className="flex items-center justify-between mt-auto">
                              <span className="text-xs text-red-600 flex items-center">
                                <i className="ri-group-line mr-1 flex-shrink-0"></i>
                                {reservation.participants}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelReservation(reservation.id, reservation.organizer);
                                }}
                                className="text-red-700 hover:text-red-900 text-xs cursor-pointer bg-white rounded-full w-5 h-5 flex items-center justify-center border border-red-300 hover:bg-red-50 transition-colors flex-shrink-0"
                                title="Rezervasyonu İptal Et"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center">
                            <div className="font-medium text-red-900 mb-1 text-xs text-center truncate w-full" title={reservation.title}>
                              {reservation.title}
                            </div>
                            <div className="text-red-600 text-xs text-center flex items-center">
                              <i className="ri-more-line mr-1"></i>
                              <span>Devam ediyor</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-gray-600 hover:text-gray-800 transition-colors">
                        <i className="ri-add-line text-xl mb-1"></i>
                        <div className="text-xs font-medium">Rezerve Et</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Toplantı Rezervasyonu</h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Oda ve Başlangıç Saati
                </label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  {rooms.find(r => r.id === selectedRoom)?.name} - {selectedTime}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Toplantı Süresi *
                </label>
                <select
                  value={meetingDuration}
                  onChange={(e) => setMeetingDuration(parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  {durationOptions.map(option => {
                    const canBook = canBookDuration(selectedRoom, selectedTime, option.value);
                    return (
                      <option 
                        key={option.value} 
                        value={option.value}
                        disabled={!canBook}
                      >
                        {option.label} {!canBook ? '(Müsait değil)' : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Seçilen süreden sonraki saat dilimleri de otomatik rezerve edilecek
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Toplantı Başlığı *
                </label>
                <input
                  type="text"
                  value={meetingTitle}
                  onChange={(e) => setMeetingTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Toplantı başlığını girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organizatör *
                </label>
                <input
                  type="text"
                  value={organizer}
                  onChange={(e) => setOrganizer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Adınızı girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şifre *
                </label>
                <input
                  type="password"
                  value={meetingPassword}
                  onChange={(e) => setMeetingPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Rezervasyon şifrenizi girin (min. 4 karakter)"
                  minLength={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bu şifre rezervasyonu iptal etmek için gerekli olacak
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Katılımcı Sayısı
                </label>
                <input
                  type="number"
                  min="1"
                  max={rooms.find(r => r.id === selectedRoom)?.capacity || 10}
                  value={participants}
                  onChange={(e) => setParticipants(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* İkram Seçenekleri */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Toplantıda İkram Olacak mı?
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setCateringEnabled(!cateringEnabled);
                      if (!cateringEnabled) {
                        setCateringType('');
                        setCateringNotes('');
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      cateringEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        cateringEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {cateringEnabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        İkram Türü *
                      </label>
                      <select
                        value={cateringType}
                        onChange={(e) => setCateringType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                      >
                        <option value="">İkram türünü seçin</option>
                        {cateringOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        İlave Notlar
                      </label>
                      <textarea
                        value={cateringNotes}
                        onChange={(e) => setCateringNotes(e.target.value)}
                        rows={3}
                        maxLength={200}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="İkram ile ilgili özel isteklerinizi yazın..."
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {cateringNotes.length}/200 karakter
                      </div>
                    </div>

                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center text-yellow-800">
                        <i className="ri-information-line mr-2"></i>
                        <span className="text-sm font-medium">Bilgilendirme</span>
                      </div>
                      <p className="text-sm text-yellow-700 mt-1">
                        İkram seçeneği aktif edildiğinde, sorumlu kişiye otomatik bildirim gönderilecektir.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                >
                  İptal
                </button>
                <button
                  onClick={handleBookMeeting}
                  disabled={!meetingTitle || !organizer || !meetingPassword || (cateringEnabled && !cateringType) || submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Kaydediliyor...
                    </>
                  ) : (
                    'Rezerve Et'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Rezervasyon İptali</h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setError('');
                  setCancelOrganizerName('');
                  setCancelPassword('');
                  setSelectedReservation(null);
                  setRelatedTimeSlots([]);
                  setSelectedSlotsToCancel([]);
                }}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Toplantı Bilgileri */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">{selectedReservation.title}</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    <span>Organizatör: {selectedReservation.organizer}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-calendar-line mr-2"></i>
                    <span>Tarih: {selectedDate}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-building-line mr-2"></i>
                    <span>Oda: {rooms.find(r => r.id === selectedReservation.room_id)?.name}</span>
                  </div>
                </div>
              </div>

              {/* Saat Dilimi Seçimi */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    İptal edilecek saat dilimleri seçin:
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={selectAllSlots}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 cursor-pointer"
                    >
                      Tümünü Seç
                    </button>
                    <button
                      onClick={selectNoneSlots}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 cursor-pointer"
                    >
                      Hiçbirini Seçme
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {relatedTimeSlots.map(slot => (
                    <label
                      key={slot}
                      className={`flex items-center p-2 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedSlotsToCancel.includes(slot)
                          ? 'bg-red-50 border-red-300 text-red-900'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSlotsToCancel.includes(slot)}
                        onChange={() => toggleSlotSelection(slot)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium">{slot}</span>
                    </label>
                  ))}
                </div>
                
                {selectedSlotsToCancel.length > 0 && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    <i className="ri-information-line mr-1"></i>
                    {selectedSlotsToCancel.length === relatedTimeSlots.length 
                      ? 'Tüm rezervasyon iptal edilecek'
                      : `${selectedSlotsToCancel.length} saat dilimi iptal edilecek (${selectedSlotsToCancel[0]} - ${selectedSlotsToCancel[selectedSlotsToCancel.length - 1]})`
                    }
                  </div>
                )}
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center text-yellow-800">
                  <i className="ri-warning-line mr-2"></i>
                  <span className="text-sm font-medium">Güvenlik Kontrolü</span>
                </div>
                <p className="text-sm text-yellow-700 mt-1">
                  Sadece toplantıyı oluşturan kişi iptal edebilir. Lütfen organizatör adınızı ve şifrenizi girin.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organizatör Adı *
                </label>
                <input
                  type="text"
                  value={cancelOrganizerName}
                  onChange={(e) => setCancelOrganizerName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Adınızı girin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şifre *
                </label>
                <input
                  type="password"
                  value={cancelPassword}
                  onChange={(e) => setCancelPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Rezervasyon şifrenizi girin"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Rezervasyonu oluştururken belirlediğiniz şifreyi girin
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setError('');
                    setCancelOrganizerName('');
                    setCancelPassword('');
                    setSelectedReservation(null);
                    setRelatedTimeSlots([]);
                    setSelectedSlotsToCancel([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                >
                  Vazgeç
                </button>
                <button
                  onClick={confirmCancelReservation}
                  disabled={!cancelOrganizerName.trim() || !cancelPassword.trim() || selectedSlotsToCancel.length === 0 || cancelSubmitting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {cancelSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      İptal Ediliyor...
                    </>
                  ) : (
                    selectedSlotsToCancel.length === relatedTimeSlots.length 
                      ? 'Rezervasyonu İptal Et'
                      : `${selectedSlotsToCancel.length} Saat Dilimini İptal Et`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}